import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle, Star, MessageSquare, ListChecks,
  ChevronRight, ChevronLeft, X, Loader2, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 60_000;

export default function SurveyPopup() {
  const { user, getAuthHeaders, currentOffice, isSuperAdmin } = useAuth();
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(-1); // -1 = intro screen
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());

  const fetchPending = useCallback(async () => {
    if (isSuperAdmin()) return;
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/surveys/employee/pending`, { headers });
      if (res.ok) setPendingSurveys(await res.json());
    } catch (e) { /* silent */ }
  }, [getAuthHeaders, currentOffice, isSuperAdmin]);

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchPending]);

  // Trigger survey based on scheduled_time
  useEffect(() => {
    if (pendingSurveys.length === 0 || activeSurvey) return;
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    for (const survey of pendingSurveys) {
      if (dismissed.has(survey.id)) continue;
      const [sh, sm] = (survey.scheduled_time || '09:00').split(':').map(Number);
      if (currentMinutes >= sh * 60 + sm) {
        setActiveSurvey(survey);
        setCurrentQ(-1);
        setAnswers({});
        break;
      }
    }
  }, [pendingSurveys, activeSurvey, dismissed]);

  // Real-time trigger for users already online
  useEffect(() => {
    if (pendingSurveys.length === 0) return;
    const checkSchedule = () => {
      if (activeSurvey) return;
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      for (const survey of pendingSurveys) {
        if (dismissed.has(survey.id)) continue;
        const [sh, sm] = (survey.scheduled_time || '09:00').split(':').map(Number);
        if (currentMinutes === sh * 60 + sm) {
          setActiveSurvey(survey);
          setCurrentQ(-1);
          setAnswers({});
          break;
        }
      }
    };
    const timer = setInterval(checkSchedule, 30_000);
    return () => clearInterval(timer);
  }, [pendingSurveys, activeSurvey, dismissed]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!activeSurvey) return;
    for (const q of activeSurvey.questions) {
      if (q.required && (answers[q.id] === undefined || answers[q.id] === '')) {
        toast.error(`Please answer: "${q.text}"`);
        const idx = activeSurvey.questions.findIndex(x => x.id === q.id);
        if (idx >= 0) setCurrentQ(idx);
        return;
      }
    }
    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const payload = {
        answers: activeSurvey.questions.map(q => ({
          question_id: q.id, answer: answers[q.id] ?? '',
        })),
      };
      const res = await fetch(`${API_URL}/api/surveys/${activeSurvey.id}/respond`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Thank you for your feedback!');
        setActiveSurvey(null);
        setAnswers({});
        setPendingSurveys(prev => prev.filter(s => s.id !== activeSurvey.id));
      } else {
        let errMsg = 'Failed to submit';
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
        toast.error(errMsg);
      }
    } catch (e) {
      toast.error(`Submission failed: ${e.message || 'Network error'}`);
    }
    finally { setSubmitting(false); }
  };

  const handleDismiss = () => {
    if (activeSurvey) {
      setDismissed(prev => new Set(prev).add(activeSurvey.id));
      setActiveSurvey(null);
      setAnswers({});
    }
  };

  if (!activeSurvey) return null;

  const questions = activeSurvey.questions || [];
  const totalQ = questions.length;
  const isIntro = currentQ === -1;
  const q = isIntro ? null : questions[currentQ];
  const progress = isIntro ? 0 : totalQ > 0 ? Math.round(((currentQ + 1) / totalQ) * 100) : 0;
  const firstName = user?.first_name || 'there';

  return (
    <div className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="survey-popup">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">

        {/* Header */}
        <div className="relative px-7 py-6 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #155179 0%, #1e74ad 60%, #2e95da 100%)' }}>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }} />

          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              {isIntro ? (
                <div className="space-y-1">
                  <p className="text-sky-200 text-sm font-medium tracking-wide">Quick Check-in</p>
                  <h2 className="text-xl font-semibold leading-snug">
                    Hi {firstName}, got a quick minute?
                  </h2>
                  <p className="text-sky-200/90 text-sm leading-relaxed mt-2">
                    I'd love your input on something simple.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-sky-200 text-sm font-medium tracking-wide">
                    Question {currentQ + 1} of {totalQ}
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-sky-200 font-mono tabular-nums w-8 text-right">{progress}%</span>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className="ml-4 mt-0.5 text-white/50 hover:text-white transition-colors rounded-lg p-1 hover:bg-white/10"
              data-testid="survey-dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6 min-h-[220px] flex flex-col justify-center" data-testid="survey-question-area">
          {isIntro ? (
            <div className="text-center space-y-5">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-50 mx-auto">
                <MessageSquare className="h-8 w-8 text-[#155179]" />
              </div>
              <div>
                <p className="text-gray-800 font-medium text-base">
                  {totalQ} quick question{totalQ !== 1 ? 's' : ''} ahead
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Your response will help us improve how we work together.
                </p>
              </div>
            </div>
          ) : q ? (
            <div className="space-y-5">
              <div>
                <p className="text-gray-900 font-medium text-base leading-relaxed">{q.text}</p>
                {q.required && (
                  <span className="inline-block mt-1 text-[11px] text-red-400 font-medium tracking-wide uppercase">Required</span>
                )}
              </div>

              {/* Multiple Choice */}
              {q.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {q.options.map((opt, oi) => (
                    <button key={oi} type="button"
                      onClick={() => handleAnswer(q.id, opt)}
                      className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all duration-200 ${
                        answers[q.id] === opt
                          ? 'bg-sky-50 border-[#1e74ad] text-[#155179] font-medium shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-[#2e95da] hover:bg-gray-50'
                      }`}
                      data-testid={`mc-option-${oi}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                          answers[q.id] === opt ? 'border-[#1e74ad] bg-[#1e74ad]' : 'border-gray-300'
                        }`}>
                          {answers[q.id] === opt && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        {opt}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Free Text */}
              {q.type === 'free_text' && (
                <textarea
                  value={answers[q.id] || ''}
                  onChange={e => handleAnswer(q.id, e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2e95da] focus:border-transparent min-h-[110px] resize-none transition-shadow"
                  data-testid="free-text-input"
                />
              )}

              {/* Rating */}
              {q.type === 'rating' && (
                <div className="flex flex-col items-center gap-3 py-2" data-testid="rating-stars">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(r => (
                      <button key={r} type="button" onClick={() => handleAnswer(q.id, r)}
                        className="p-1 transition-all duration-200 hover:scale-125 active:scale-95"
                        data-testid={`star-${r}`}>
                        <Star className={`h-9 w-9 transition-colors ${
                          (answers[q.id] || 0) >= r
                            ? 'fill-amber-400 text-amber-400 drop-shadow-sm'
                            : 'text-gray-300 hover:text-amber-300'
                        }`} />
                      </button>
                    ))}
                  </div>
                  {answers[q.id] && (
                    <span className="text-sm text-gray-500 font-medium">
                      {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][answers[q.id]]}
                    </span>
                  )}
                </div>
              )}

              {/* Yes / No */}
              {q.type === 'yes_no' && (
                <div className="flex gap-4">
                  <button type="button" onClick={() => handleAnswer(q.id, true)}
                    className={`flex-1 py-4 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                      answers[q.id] === true
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-700 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50'
                    }`} data-testid="yes-btn">
                    <CheckCircle className={`h-6 w-6 mx-auto mb-1.5 transition-colors ${answers[q.id] === true ? 'text-emerald-500' : 'text-gray-300'}`} />
                    Yes
                  </button>
                  <button type="button" onClick={() => handleAnswer(q.id, false)}
                    className={`flex-1 py-4 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                      answers[q.id] === false
                        ? 'bg-rose-50 border-rose-400 text-rose-700 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-rose-300 hover:bg-rose-50/50'
                    }`} data-testid="no-btn">
                    <X className={`h-6 w-6 mx-auto mb-1.5 transition-colors ${answers[q.id] === false ? 'text-rose-500' : 'text-gray-300'}`} />
                    No
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-7 py-4 flex items-center justify-between bg-gray-50/60">
          {isIntro ? (
            <>
              <button
                onClick={handleDismiss}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="survey-maybe-later"
              >
                Maybe later
              </button>
              <button
                onClick={() => setCurrentQ(0)}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                style={{ backgroundColor: '#155179' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1e74ad'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#155179'}
                data-testid="survey-start-btn"
              >
                Sure, let's go
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setCurrentQ(p => Math.max(-1, p - 1))}
                disabled={currentQ <= 0}
                className={`inline-flex items-center gap-1 text-sm transition-colors ${
                  currentQ <= 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="survey-prev-btn"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>

              {currentQ < totalQ - 1 ? (
                <button
                  onClick={() => setCurrentQ(p => Math.min(totalQ - 1, p + 1))}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                  style={{ backgroundColor: '#155179' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1e74ad'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#155179'}
                  data-testid="survey-next-btn"
                >
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                  style={{ backgroundColor: '#155179' }}
                  onMouseEnter={e => { if (!submitting) e.currentTarget.style.backgroundColor = '#1e74ad'; }}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#155179'}
                  data-testid="survey-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Submit
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
